/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import { generateClient } from 'aws-amplify/data';

let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

export const chatService = {
  async listMessages() {
    try {
      const { data: messages } = await getClient().models.Message.list({
        sortDirection: 'ASC',
      });
      
      return messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        username: msg.username,
        userId: msg.userId,
        createdAt: msg.createdAt,
      }));
    } catch (error) {
      console.error('Failed to list messages:', error);
      throw new Error('Failed to load messages');
    }
  },

  async sendMessage(content, username, userId) {
    try {
      const { data: message, errors } = await getClient().models.Message.create({
        content,
        username,
        userId,
        createdAt: new Date().toISOString(),
      });

      if (errors) throw new Error(errors[0].message);

      return {
        id: message.id,
        content: message.content,
        username: message.username,
        userId: message.userId,
        createdAt: message.createdAt,
      };
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  },

  // Subscribe to real-time updates with automatic fallback
  subscribeToMessages(callback, onError) {
    let subscription = null;
    let pollInterval = null;
    let isSubscriptionActive = false;

    const startPolling = () => {
      console.warn('📡 Chat: Falling back to polling mode');
      const poll = async () => {
        try {
          const messages = await this.listMessages();
          callback(messages, false);
        } catch (error) {
          console.error('Polling error:', error);
          onError?.(error);
        }
      };

      poll(); // Initial load
      pollInterval = setInterval(poll, 5000);
    };

    try {
      subscription = getClient().models.Message.observeQuery().subscribe({
        next: ({ items, isSynced }) => {
          isSubscriptionActive = true;
          
          // Clear polling if subscription becomes active
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }

          const messages = items
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .map(msg => ({
              id: msg.id,
              content: msg.content,
              username: msg.username,
              userId: msg.userId,
              createdAt: msg.createdAt,
            }));
          
          callback(messages, isSynced);
        },
        error: (error) => {
          console.error('❌ Subscription error:', error);
          onError?.(error);
          
          // Fallback to polling on subscription error
          if (!pollInterval) {
            startPolling();
          }
        },
      });

      // Fallback to polling if subscription doesn't activate within 3s
      setTimeout(() => {
        if (!isSubscriptionActive && !pollInterval) {
          startPolling();
        }
      }, 3000);

    } catch (error) {
      console.error('Failed to create subscription:', error);
      onError?.(error);
      startPolling();
    }

    // Return cleanup function
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  },
};
